import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, ShieldCheck, ShieldAlert } from "lucide-react";
import { Badge } from "../ui/badge";
import { fmtDate } from "./leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColor = {
  admin: "bg-purple-600/20 text-purple-400 border-purple-500/40",
  customer: "bg-blue-600/20 text-blue-400 border-blue-500/40",
  tech: "bg-cyan-600/20 text-cyan-400 border-cyan-500/40",
};

const shortAgent = (ua) => {
  if (!ua) return "";
  if (/iPhone|iPad/i.test(ua)) return "iPhone/iPad";
  if (/Android/i.test(ua)) return "Android";
  if (/Macintosh/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Linux/i.test(ua)) return "Linux";
  return "Other";
};

export const ActivityTab = ({ onAuthError }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("All");

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/login-activity`, { params: roleFilter ? { role: roleFilter } : {} });
      setEvents(res.data);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [onAuthError, roleFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const filtered = events.filter((e) => {
    if (resultFilter === "Success" && !e.success) return false;
    if (resultFilter === "Failed" && e.success) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="activity-tab">
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <select data-testid="activity-filter-role" className="h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="customer">Customers</option>
          <option value="tech">Techs</option>
        </select>
        <select data-testid="activity-filter-result" className="h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm" value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
          <option>All</option><option>Success</option><option>Failed</option>
        </select>
        <p className="text-zinc-600 text-xs ml-auto">Last 300 sign-in events</p>
      </div>
      {filtered.length === 0 ? (
        <div data-testid="activity-empty-state" className="text-center py-24 text-zinc-500"><Inbox className="h-12 w-12 mx-auto mb-4 text-zinc-700" />No sign-in activity yet.</div>
      ) : (
        <div className="space-y-2" data-testid="activity-list">
          {filtered.map((e) => (
            <div key={e.id} data-testid={`activity-row-${e.id}`} className={`bg-[#121212] border rounded-sm p-3 flex flex-wrap items-center gap-3 ${e.success ? "border-zinc-800" : "border-red-500/30"}`}>
              {e.success
                ? <ShieldCheck className="h-4 w-4 text-green-500 shrink-0" />
                : <ShieldAlert className="h-4 w-4 text-red-500 shrink-0" />}
              <Badge variant="outline" className={`${roleColor[e.role] || roleColor.customer} rounded-sm uppercase text-[10px]`}>{e.role}</Badge>
              <span className="text-white text-sm font-medium">{e.identifier}</span>
              <span className={`text-xs font-semibold ${e.success ? "text-green-400" : "text-red-400"}`}>
                {e.success ? "Signed in" : "FAILED"}{e.detail ? ` · ${e.detail}` : ""}
              </span>
              <span className="text-zinc-500 text-xs ml-auto">{e.ip} · {shortAgent(e.user_agent)} · {fmtDate(e.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
